import React, { useEffect, useState, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Row } from 'antd';
import { MESSAGES, STATUSES } from '@guuru/chat-common';
import events from '@guuru/events-web';
import { i18n as translations } from '@guuru/translation-web';
import ChatRenderText from '../Text';
import Option from '../../Option';
import useWaitTimeout from './hooks/useWaitTimeout';
import TRANSFER_BY_EMAIL from './mutations/TransferByEmail';
import GET_CHAT from './queries/GetChatWaitTimeout';

interface ChatRenderWaitTimeoutProps {
  chatId: string;
  message: {
    id: string;
    i18n: string;
    createdAt: number;
    metadata: {
      waitTimeoutStatus: string;
    };
  };
  readOnly?: boolean;
}

const ChatRenderWaitTimeout = function ({
  message,
  chatId,
  readOnly = false,
}: ChatRenderWaitTimeoutProps) {
  const { id, i18n, createdAt } = message;
  const waitTimeoutStatus = message
    ?.metadata
    ?.waitTimeoutStatus as keyof typeof MESSAGES;

  const [transfer] = useMutation(TRANSFER_BY_EMAIL);
  const [
    newMessage,
    setNewMessage,
  ] = useState<React.ComponentProps<typeof ChatRenderText>['message']>();
  const [
    currentTimeoutStatus,
    setCurrentTimeoutStatus,
  ] = useState<keyof typeof MESSAGES>(MESSAGES.emailWaitTimeout);
  const [currentChatStatus, setCurrentChatStatus] = useState('');

  const { data, loading } = useQuery(GET_CHAT, {
    variables: {
      id: chatId,
    },
  });

  const status = data?.chat?.status ?? STATUSES.created;

  const shouldSkipSubscribe = currentTimeoutStatus !== MESSAGES.emailWaitTimeout
    || ![STATUSES.created, STATUSES.assigned].includes(currentChatStatus);

  const waitTimeoutUpdate = useWaitTimeout(id, chatId, shouldSkipSubscribe);

  const handleClick = useCallback(async (mutation: typeof transfer) => {
    setCurrentChatStatus('handover');
    events.transferByEmail();
    await mutation({ variables: { chatId } });
  }, [chatId, setCurrentChatStatus]);

  const triggerAnalytics = useCallback(() => {
    if (![STATUSES.created, STATUSES.assigned].includes(currentChatStatus)) {
      return null;
    }
    return events.emailWaitTimeout();
  }, [currentChatStatus]);

  useEffect(() => {
    setNewMessage({ i18n, type: MESSAGES.waitTimeout, createdAt });
    setCurrentTimeoutStatus(waitTimeoutStatus);
    triggerAnalytics();
  }, [
    i18n,
    waitTimeoutStatus,
    triggerAnalytics,
    createdAt,
  ]);

  useEffect(() => {
    setCurrentChatStatus(status);
  }, [status]);

  useEffect(() => {
    if (waitTimeoutUpdate) {
      setNewMessage({
        i18n: waitTimeoutUpdate.text ?? '',
        type: MESSAGES.waitTimeout,
        createdAt,
      });
      setCurrentTimeoutStatus(waitTimeoutUpdate.waitTimeoutStatus);
      triggerAnalytics();
    }
  }, [
    waitTimeoutUpdate,
    triggerAnalytics,
    createdAt,
  ]);

  if (loading || newMessage === undefined) {
    return null;
  }

  const transferOptions = {
    key: 'transferByEmailOption',
    translatedText: translations.t('bot:::transferByEmail'),
    action: transfer,
  } as const;

  return (
    <ChatRenderText message={newMessage} tooltip={false}>
      {([STATUSES.created, STATUSES.assigned].includes(currentChatStatus))
      && !readOnly
      && (
        <Row className="message-options">
          <ul className="message-options-list">
            <Option
              id={transferOptions.key}
              name={transferOptions.key}
              onClick={() => handleClick(transferOptions.action)}
            >
              {transferOptions.translatedText}
            </Option>
          </ul>
        </Row>
      )}
    </ChatRenderText>
  );
};

export default ChatRenderWaitTimeout;
